import * as React from 'react';
import styled from '@emotion/styled';
import { StaticQuery, graphql } from 'gatsby';
import Link from './link';
import Loadable from 'react-loadable';

import config from '../../config.js';
import LoadingProvider from './mdxComponents/loading';
import { DarkModeSwitch } from './DarkModeSwitch';

const isSearchEnabled = config.header.search && config.header.search.enabled ? true : false;

let searchIndices = [];

if (isSearchEnabled && config.header.search.indexName) {
  searchIndices.push({
    name: `${config.header.search.indexName}`,
    title: `Results`,
    hitComp: `PageHit`,
  });
}

import Sidebar from './sidebar';

const LoadableComponent = Loadable({
  loader: () => import('./search/index'),
  loading: LoadingProvider,
});

/**
 * Header Styles - Updated to match Figma design
 * Design: node-id=1-6314
 */
const HeaderWrapper = styled('div')`
  background-color: var(--color-bg);
  border-bottom: 1px solid var(--color-border-secondary);
  position: sticky;
  top: 0;
  z-index: 500;

  /* Position context for mobile dropdown */
  // position: relative;
`;

const HeaderNav = styled('nav')`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--gap-4x, 20px);
  padding: 12px var(--gap-6x, 30px);
  min-height: var(--header-height);
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: var(--gap-2x, 10px);
  }

  @media (max-width: 992px) {
    min-height: var(--header-height-mobile);
  }
`;

const LogoSection = styled('div')`
  display: flex;
  align-items: center;
  gap: var(--gap-2x, 10px);
  flex-shrink: 0;
  width: 309px;

  @media (max-width: 992px) {
    width: auto;
    gap: var(--gap-2x, 10px);
  }
`;

const LogoContainer = styled('div')`
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }

  /* Logo circle color - inverts based on theme */
  svg path {
    fill: var(--color-info);
  }

  @media (max-width: 992px) {
    width: 40px;
    height: 40px;
  }
`;

// Search Icon SVG for mobile
const SearchIconSvg = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_mobile_search)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.8254 2.83347C5.51643 2.83347 2.83398 5.51591 2.83398 8.82488C2.83398 12.1339 5.51643 14.8163 8.8254 14.8163C12.1344 14.8163 14.8168 12.1339 14.8168 8.82488C14.8168 5.51591 12.1344 2.83347 8.8254 2.83347ZM1.33398 8.82488C1.33398 4.68749 4.68801 1.33347 8.8254 1.33347C12.9628 1.33347 16.3168 4.68749 16.3168 8.82488C16.3168 10.7125 15.6187 12.437 14.4666 13.7544L16.6869 15.9689C16.9801 16.2615 16.9808 16.7363 16.6882 17.0296C16.3957 17.3229 15.9209 17.3235 15.6276 17.031L13.3709 14.7802C12.1103 15.7438 10.5347 16.3163 8.8254 16.3163C4.68801 16.3163 1.33398 12.9623 1.33398 8.82488Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_mobile_search">
        <rect width="18" height="18" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// Inline SVG Logo - feather/fern with circular background
const LogoSvg = () => (
  <svg preserveAspectRatio="none" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M50 25C50 38.8071 38.8071 50 25 50C11.1929 50 0 38.8071 0 25C0 11.1929 11.1929 0 25 0C29.5021 0 33.7262 1.19004 37.375 3.27273C38.0184 3.63999 38.644 4.03502 39.25 4.45617C40.1721 5.09699 41.049 5.7983 41.875 6.55435C46.8685 11.1252 50 17.6969 50 25ZM10.1897 43.6112C10.0626 43.8627 9.77742 44.8977 10.3171 44.9946C11.0767 45.1311 12.1362 42.6396 13.1093 40.3513C13.9299 38.4215 14.689 36.6363 15.155 36.6939C16.1735 36.8196 25.4485 37.7581 25.7221 36.3165C25.8508 35.6385 23.7351 35.5006 21.5028 35.3551C18.9891 35.1913 16.3275 35.0179 16.5555 34.0527C16.7762 33.1182 19.2129 33.5529 21.6645 33.9903C24.131 34.4303 26.6125 34.873 26.8679 33.9269C27.1368 32.9307 24.6365 32.671 22.2729 32.4255C20.1607 32.2061 18.1576 31.998 18.3379 31.2858C18.5232 30.5536 20.5361 30.9462 22.6471 31.3579C24.8876 31.7949 27.2385 32.2534 27.6318 31.4115C28.0312 30.5568 25.9947 30.2176 24.033 29.8909C22.2423 29.5927 20.5138 29.3048 20.7568 28.6446C21.0115 27.9529 22.953 28.4874 24.8945 29.0219C26.8361 29.5564 28.7776 30.091 29.0322 29.3992C29.2886 28.7027 27.2862 28.2294 25.4131 27.7866C23.5656 27.3499 21.8438 26.9428 22.5392 26.3808C23.2601 25.7981 25.0606 26.4152 26.7076 26.9797C28.2602 27.5118 29.6763 27.9972 29.9234 27.3869C30.2095 26.6805 28.3423 26.068 26.7545 25.5472C25.5156 25.1408 24.4468 24.7902 24.7036 24.4942C25.0795 24.061 26.5652 24.6328 28.0534 25.2055C29.5894 25.7966 31.1279 26.3887 31.4512 25.8777C31.8049 25.3187 29.9973 24.6045 28.4518 23.9938C27.2153 23.5052 26.1465 23.0828 26.486 22.8592C26.8293 22.6331 27.9957 23.0168 29.2105 23.4164C30.6986 23.9059 32.2595 24.4193 32.4697 23.8654C32.6819 23.3066 31.2446 22.7866 30.0337 22.3485C29.0643 21.9977 28.24 21.6995 28.523 21.4758C28.8054 21.2526 29.8145 21.4997 30.9056 21.7669C32.2737 22.102 33.7707 22.4686 34.1248 21.9788C34.4641 21.5096 33.212 21.1118 32.0649 20.7474C31.0598 20.4281 30.1354 20.1344 30.4327 19.8408C30.7292 19.5479 31.6885 19.8007 32.6932 20.0654C33.8457 20.369 35.0579 20.6884 35.398 20.2181C35.7262 19.7641 34.6325 19.4439 33.5835 19.1367C32.5982 18.8482 31.6522 18.5713 31.9605 18.2058C32.281 17.8259 33.3115 18.0198 34.3208 18.2097C35.3161 18.397 36.2909 18.5805 36.5438 18.2058C36.8107 17.8103 35.7835 17.622 34.8004 17.4419C33.9079 17.2784 33.0519 17.1215 33.2336 16.8223C33.4099 16.5321 34.3453 16.6169 35.3267 16.7058C36.472 16.8096 37.6798 16.9191 37.8169 16.445C37.9478 15.9924 36.8338 15.9054 35.8067 15.8253C34.8359 15.7495 33.9428 15.6798 34.2521 15.3131C34.5726 14.9331 35.6032 14.9677 36.5475 14.9993C37.4787 15.0305 38.3262 15.0589 38.3262 14.6842C38.3262 14.3069 37.5623 14.3069 36.8143 14.3069C36.0664 14.3069 35.3343 14.3069 35.398 13.9296C35.4614 13.5534 36.411 13.4898 37.3474 13.4271C38.2891 13.364 39.2174 13.3018 39.2174 12.9235C39.2174 12.5277 38.3771 12.5816 37.5962 12.6317C36.888 12.6772 36.2286 12.7195 36.2892 12.4204C36.3491 12.1243 37.0865 12.0234 37.8369 11.9207C38.68 11.8053 39.5394 11.6877 39.472 11.2885C39.4037 10.8838 38.6766 11.0939 38.0364 11.2789C37.4826 11.4389 36.994 11.5801 37.053 11.2885C37.1146 10.9845 37.771 10.7687 38.4184 10.5558C39.1105 10.3283 39.7924 10.1041 39.7266 9.77923C39.6583 9.44151 39.1125 9.6479 38.6219 9.83346C38.1989 9.99342 37.8169 10.1379 37.8169 9.905C37.8169 9.67015 38.3441 9.35307 38.8803 9.03054C39.4927 8.66217 40.117 8.28668 39.9813 8.01845C39.8438 7.74681 39.3722 8.09853 38.9675 8.40043C38.6226 8.65767 38.3262 8.87874 38.3262 8.6473C38.3262 8.40198 38.7197 8.12676 39.0935 7.86537C39.4862 7.59074 39.8571 7.33139 39.7266 7.13806C39.5925 6.93934 39.2818 7.12437 39.0364 7.27056C38.8158 7.40194 38.6479 7.50195 38.7081 7.26383C38.7377 7.14678 38.8018 6.96164 38.8763 6.74644C39.122 6.03672 39.4808 5 39.0901 5C38.7917 5 38.6682 5.69051 38.5658 6.26255C38.4934 6.66705 38.4316 7.0123 38.3262 7.0123C38.2214 7.0123 38.2029 6.82078 38.1819 6.60405C38.1518 6.29389 38.1167 5.93212 37.8169 6.00615C37.5409 6.07432 37.6763 6.73374 37.7974 7.3234C37.8997 7.82159 37.9918 8.27 37.8169 8.27C37.6289 8.27 37.5643 7.93469 37.5016 7.60923C37.4369 7.27361 37.3743 6.94846 37.1803 7.0123C36.991 7.07465 37.052 7.6315 37.1151 8.20791C37.1792 8.79426 37.2456 9.40085 37.053 9.52769C36.8736 9.64589 36.8346 9.31975 36.791 8.95382C36.7417 8.54101 36.6864 8.07756 36.4165 8.14423C36.1646 8.20643 36.2864 8.85312 36.4123 9.52137C36.541 10.2043 36.6739 10.9098 36.4165 11.0369C36.1753 11.156 36.1055 10.5701 36.0311 9.94686C35.9485 9.25394 35.8604 8.51488 35.5253 8.6473C35.207 8.77307 35.3979 9.52768 35.5889 10.2823C35.7799 11.0369 35.9709 11.7915 35.6526 11.9173C35.3342 12.0431 35.2739 11.5081 35.2118 10.9577C35.1498 10.4076 35.0861 9.84214 34.7614 9.905C34.4379 9.96762 34.5944 10.7787 34.7545 11.6084C34.9159 12.4449 35.081 13.3003 34.7614 13.4265C34.4555 13.5474 34.3848 12.9424 34.3091 12.2951C34.2273 11.5952 34.1397 10.8458 33.7429 10.9112C33.344 10.9768 33.5005 12.0027 33.6506 12.9859C33.7879 13.8857 33.9197 14.7499 33.6156 14.81C33.3114 14.8701 33.2107 14.2409 33.1051 13.5812C32.9898 12.8604 32.8686 12.1032 32.4697 12.1688C32.0908 12.2312 32.2758 13.1603 32.4653 14.1115C32.6577 15.0777 32.8547 16.0667 32.4697 16.1935C32.102 16.3146 31.9998 15.4737 31.8933 14.5971C31.7785 13.6529 31.6587 12.6673 31.1966 12.7977C30.7346 12.9281 30.9227 14.1739 31.1046 15.3786C31.2735 16.4978 31.4372 17.5815 31.0693 17.7027C30.7252 17.816 30.6652 17.0105 30.5986 16.114C30.5172 15.0204 30.4258 13.7914 29.7961 13.9296C29.1914 14.0624 29.4023 15.4914 29.6054 16.8669C29.787 18.0976 29.9623 19.2853 29.5415 19.4635C29.1325 19.6366 29.0185 18.7238 28.8918 17.7096C28.7423 16.5138 28.5753 15.177 27.8864 15.3131C27.2158 15.4456 27.4989 16.939 27.7683 18.3597C28.0103 19.636 28.2411 20.8536 27.7591 20.9727C27.2624 21.0954 27.0988 19.932 26.9286 18.7223C26.7498 17.4513 26.5638 16.129 25.9767 16.1935C25.3872 16.2582 25.6401 17.8207 25.886 19.34C26.1181 20.7739 26.3439 22.1693 25.8494 22.2304C25.3682 22.2898 25.2564 21.1141 25.1382 19.8704C25.0061 18.4814 24.866 17.0075 24.1943 17.0738C23.5205 17.1404 23.7382 19.039 23.9416 20.8117C24.1224 22.3883 24.2918 23.8654 23.8124 23.8654C23.3546 23.8654 23.2311 22.7982 23.0953 21.6231C22.9288 20.1839 22.7437 18.5831 21.9027 18.5831C21.0744 18.5831 21.4436 20.5423 21.793 22.3965C22.088 23.9624 22.369 25.4534 21.9027 25.6262C21.5066 25.7729 21.2622 24.6185 20.9857 23.3121C20.6285 21.6243 20.2176 19.683 19.3564 19.9665C18.7949 20.1514 19.179 22.268 19.548 24.3011C19.8978 26.2282 20.2339 28.0803 19.7383 28.1415C19.3148 28.1938 19.0915 26.9085 18.833 25.4209C18.5042 23.5289 18.1186 21.3097 17.192 21.0985C16.2895 20.8927 16.8257 23.4922 17.3346 25.9597C17.759 28.0168 18.1644 29.9823 17.7013 30.1538C17.359 30.2807 16.9831 28.8931 16.5553 27.3138C15.9837 25.2036 15.3194 22.7511 14.5184 23.1108C13.972 23.3561 14.4804 25.806 14.9828 28.2272C15.4858 30.6512 15.9829 33.0465 15.4096 33.1723C14.8789 33.2888 14.4027 31.5161 13.8926 29.617C13.3015 27.4165 12.6649 25.0463 11.8449 25.2488C10.9158 25.4783 11.9169 29.2422 12.7298 32.2986C13.2537 34.2684 13.6994 35.9443 13.4999 36.1908C13.1073 36.6759 11.4263 40.6785 10.6199 42.5986C10.381 43.1676 10.2188 43.5537 10.1897 43.6112Z" />
  </svg>
);

const LogoTitle = styled('span')`
  font-family: var(--font-family-primary, 'Inter', sans-serif);
  font-size: var(--font-size-lg, 18px);
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-info);

  @media (max-width: 992px) {
    font-size: 16.8px;
  }
`;

const SearchSection = styled('div')`
  width: 400px;
  flex-shrink: 0;
  position: relative;

  @media (max-width: 992px) {
    display: none;
  }
`;

/**
 * Mobile Search Icon Button - Shows only on mobile
 * Triggers the mobile search overlay
 */
const MobileSearchButton = styled('button')`
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: var(--color-info);
  transition: opacity var(--transition-fast, 0.15s ease);

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 992px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

/**
 * Mobile Search Backdrop - Overlay below header to capture outside clicks
 * z-index 501 is just above header (500) but below results panel (1001)
 */
const MobileSearchBackdrop = styled('div')`
  display: none;

  @media (max-width: 992px) {
    display: ${props => props.isActive ? 'block' : 'none'};
    position: fixed;
    top: var(--header-height-mobile);
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--color-bg-grey-container);
    opacity: 0.4;
    z-index: 501;
  }
`;

/**
 * Mobile Search Overlay - Full width search bar that covers the header
 * Positioned absolutely to overlay other header elements
 */
const MobileSearchOverlay = styled('div')`
  display: none;

  @media (max-width: 992px) {
    display: ${props => props.isActive ? 'flex' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--header-height-mobile);
    background: var(--color-bg);
    padding: var(--gap-4x, 20px) var(--gap-3x, 15px);
    z-index: 502;
    align-items: center;

    /* Style the search input container for mobile - transparent border by default, green on focus */
    form {
      background: var(--color-bg-box);
      border: 1px solid transparent;

      &:focus-within {
        border-color: var(--color-primary);
        box-shadow: none;
      }
    }
  }
`;

const RightSection = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--gap-3x, 15px);
  flex: 1;
  min-width: 0;

  @media (max-width: 992px) {
    gap: var(--gap-4x, 20px);
  }

  @media (max-width: 576px) {
    .theme-label {
      display: none;
    }
  }
`;

const HeaderLinks = styled('ul')`
  display: flex;
  align-items: center;
  gap: var(--gap-2x, 10px);
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    list-style: none;
  }

  a {
    color: var(--color-info-secondary);
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: var(--font-size-sm, 14px);
    font-weight: 500;
    text-decoration: none;
    padding: var(--gap-2x, 10px);
    transition: color var(--transition-fast, 0.15s ease);

    &:hover {
      color: var(--color-primary);
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled('button')`
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: var(--gap-2x, 10px);

  @media (max-width: 992px) {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .iconBar {
    display: block;
    width: 22px;
    height: 2.5px;
    background-color: var(--color-info-tertiary);
    border-radius: 1px;
    transition: all var(--transition-fast, 0.15s ease);
  }

  &:hover .iconBar {
    background-color: var(--color-info);
  }
`;

const MobileNav = styled('div')`
  display: none;
  max-height: 0;
  overflow: hidden;
  transition: max-height var(--transition-normal, 0.3s ease);

  @media (max-width: 992px) {
    &.open {
      display: flex;
      flex-direction: column;
      max-height: none;
      min-height: calc(100vh - var(--header-height-mobile));
      overflow-y: auto;
      position: absolute;
      top: var(--header-height-mobile);
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--color-bg-container);
      border-top: 1px solid var(--color-border-secondary);
      padding: 0;
      z-index: 1000;
    }
  }

  /* Mobile sidebar container - takes full height */
  .mobileSidebarContainer {
    flex: 1;
    padding: var(--gap-3x, 15px);
    background-color: var(--color-bg);
  }

  /* Mobile nav link styling */
  .mobileNavLink {
    display: block;
    padding: var(--gap-2x, 10px) var(--gap-4x, 20px);
    color: var(--color-info-secondary);
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: var(--font-size-lg, 18px);
    font-weight: 500;
    text-decoration: none;
    transition: all var(--transition-fast, 0.15s ease);

    &:hover {
      background: var(--color-bg-box);
      color: var(--color-primary);
    }
  }

  /* Override sidebar styles for mobile */
  .sideBarUL {
    margin-top: 0;
    padding: 0;
  }

  .sidebarTitle {
    display: none;
  }
`;

const Header = ({ location, isDarkThemeActive, toggleActiveTheme }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [mobileSearchActive, setMobileSearchActive] = React.useState(false);

  // Prevent body scroll when mobile menu is open
  React.useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const openMobileSearch = () => {
    setMobileSearchActive(true);
  };

  const closeMobileSearch = () => {
    setMobileMenuOpen(false)
    setMobileSearchActive(false);
    // Reset body overflow in case search component set it
    document.body.style.overflow = '';
  };

  return (
    <StaticQuery
      query={graphql`
        query headerTitleQuery {
          site {
            siteMetadata {
              headerTitle
              helpUrl
              tweetText
              logo {
                link
                image
              }
              headerLinks {
                link
                text
              }
            }
          }
        }
      `}
      render={(data) => {
        const {
          site: {
            siteMetadata: { headerTitle, logo, headerLinks },
          },
        } = data;

        const finalLogoLink = logo.link !== '' ? logo.link : '/';

        return (
          <HeaderWrapper>
            <HeaderNav>
              {/* Logo Section */}
              <LogoSection>
                <Link to={finalLogoLink}>
                  <LogoContainer>
                    <LogoSvg />
                  </LogoContainer>
                </Link>
                <Link to={finalLogoLink} style={{ textDecoration: 'none' }}>
                  <LogoTitle dangerouslySetInnerHTML={{ __html: headerTitle }} />
                </Link>
              </LogoSection>

              {/* Search Section */}
              {isSearchEnabled && (
                <SearchSection>
                  <LoadableComponent collapse={true} indices={searchIndices} />
                </SearchSection>
              )}

              {/* Right Section - Links and Theme Toggle */}
              <RightSection>
                <HeaderLinks>
                  {headerLinks.map((link, key) => {
                    if (link.link !== '' && link.text !== '') {
                      return (
                        <li key={key}>
                          <a
                            href={link.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            dangerouslySetInnerHTML={{ __html: link.text }}
                          />
                        </li>
                      );
                    }
                    return null;
                  })}
                </HeaderLinks>
                {/* Mobile Search Button - Only visible on mobile */}
                {isSearchEnabled && (
                  <MobileSearchButton
                    onClick={openMobileSearch}
                    aria-label="Open search"
                  >
                    <SearchIconSvg />
                  </MobileSearchButton>
                )}
                <DarkModeSwitch
                  isDarkThemeActive={isDarkThemeActive}
                  toggleActiveTheme={toggleActiveTheme}
                />
                <MobileMenuButton
                  onClick={toggleMobileMenu}
                  aria-label="Toggle mobile menu"
                  aria-expanded={mobileMenuOpen}
                >
                  <span className="iconBar"></span>
                  <span className="iconBar"></span>
                  <span className="iconBar"></span>
                </MobileMenuButton>
              </RightSection>

              {/* Mobile Search Overlay - Covers header when active */}
              {isSearchEnabled && (
                <MobileSearchOverlay isActive={mobileSearchActive}>
                  <LoadableComponent
                    collapse={true}
                    indices={searchIndices}
                    onClose={closeMobileSearch}
                    autoFocus={mobileSearchActive}
                  />
                </MobileSearchOverlay>
              )}
            </HeaderNav>

            {/* Mobile Navigation - Full page overlay */}
            <MobileNav className={mobileMenuOpen ? 'open' : ''}>
              <div className="mobileSidebarContainer">
                <Sidebar location={location} onNavigate={() => setMobileMenuOpen(false)} />
              </div>
            </MobileNav>

            {/* Mobile Search Backdrop - Closes search when clicking outside */}
            {isSearchEnabled && (
              <MobileSearchBackdrop
                isActive={mobileSearchActive}
                onClick={closeMobileSearch}
              />
            )}
          </HeaderWrapper>
        );
      }}
    />
  );
};

export default Header;
